<template>
  <div class="planets-drag-drop-game">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <!-- Drag and Drop Game Area -->
      <div class="game-area">
        <!-- Left Side: Draggable Planets -->
        <div class="draggable-planets">
          <div
            v-for="(planet) in shuffledPlanets"
            :key="planet.name"
            class="planet-draggable"
            :style="getPlanetStyle(planet)"
            draggable="false"
            @mousedown="onMouseDown($event, planet)"
            @touchstart.prevent="onTouchStart($event, planet)"
            @click="onPlanetClick(planet)"
          >
            <img
              :src="planet.image"
              :alt="planet.name"
            >
            <span>{{ planet.name }}</span>
          </div>
        </div>

        <!-- Right Side: Drop Zones -->
        <div class="drop-area">
          <!-- Drop Zones for Planets -->
          <div
            v-for="(planet) in currentPlanets"
            :key="planet.name"
            class="planet-dropzone"
            :data-planet-name="planet.name"
          >
            <!-- Show the planet if it's placed -->
            <img
              v-if="planet.placed"
              :src="planet.image"
              :alt="planet.name"
              class="placed-planet"
            >
            <!-- Show placeholder if not placed -->
            <div
              v-else
              class="planet-placeholder"
            />
          </div>
        </div>
      </div>

      <!-- Message Display -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
    </div>

    <!-- Dragging Clone Element -->
    <div
      v-if="dragging && draggingPlanet"
      :style="{
        position: 'fixed',
        left: dragPosition.x + 'px',
        top: dragPosition.y + 'px',
        zIndex: 1000,
        pointerEvents: 'none',
      }"
      class="dragging-clone planet-draggable"
    >
      <img
        :src="draggingPlanet.image"
        :alt="draggingPlanet.name"
      >
      <span>{{ draggingPlanet.name }}</span>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

import EarthImage from "@/assets/planets/Earth.png";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      planetGroups: [
        [
          // Group 1: First 4 Planets
          {
            name: "Mercury",
            image:
              "https://png.pngtree.com/png-clipart/20230801/original/pngtree-mercury-planet-isolated-on-white-background-picture-image_7805941.png",
            placed: false,
          },
          {
            name: "Venus",
            image:
              "https://images.vexels.com/media/users/3/205478/isolated/lists/4a42dba3e55a9bcc9764720d87f2ca93-planet-venus-illustration.png",
            placed: false,
          },
          {
            name: "Earth",
            image: EarthImage,
            placed: false,
          },
          {
            name: "Mars",
            image:
              "https://www.pngplay.com/wp-content/uploads/4/Mars-PNG-Images-HD.png",
            placed: false,
          },
        ],
        [
          // Group 2: Remaining Planets
          {
            name: "Jupiter",
            image: "https://cdn-icons-png.flaticon.com/512/124/124558.png",
            placed: false,
          },
          {
            name: "Saturn",
            image:
              "https://img.pikbest.com/origin/09/22/96/52MpIkbEsTJDS.png!sw800",
            placed: false,
          },
          {
            name: "Uranus",
            image:
              "https://www.pngplay.com/wp-content/uploads/13/Uranus-PNG-HD-Quality.png",
            placed: false,
          },
          {
            name: "Neptune",
            image:
              "https://static.vecteezy.com/system/resources/previews/020/004/117/non_2x/neptune-planet-illustration-png.png",
            placed: false,
          },
        ],
      ],
      currentGroupIndex: 0,
      currentPlanets: [], // Planets in the current group
      shuffledPlanets: [], // Shuffled draggable planets in the current group
      isGameComplete: false,
      message: "",
      selectedVoice: null,
      // Added for dragging
      draggingPlanet: null,
      dragStartOffset: { x: 0, y: 0 },
      dragging: false,
      dragPosition: { x: 0, y: 0 },
    };
  },
  mounted() {
    this.setupGame();
    this.setupVoices();
    // Add event listeners for dragging
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("touchmove", this.onTouchMove);
    window.addEventListener("touchend", this.onTouchEnd);
  },
  beforeUnmount() {
    // Remove event listeners when component is destroyed
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("touchmove", this.onTouchMove);
    window.removeEventListener("touchend", this.onTouchEnd);
  },
  methods: {
    setupGame() {
      // Reset the game state for the current group
      this.currentPlanets = this.planetGroups[this.currentGroupIndex].map(
        (planet) => ({ ...planet, placed: false })
      );
      this.isGameComplete = false;
      this.message = "";
      // Shuffle the planets for the draggable list
      this.shuffledPlanets = this.shuffle([...this.currentPlanets]);
    },
    shuffle(array) {
      let currentIndex = array.length,
        temporaryValue,
        randomIndex;

      // While there remain elements to shuffle...
      while (0 !== currentIndex) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }
      return array;
    },
    onPlanetClick(planet) {
      // Speak the planet's name when clicked
      this.speakOutLoud(planet.name);
    },
    // Mouse and Touch Event Handlers
    onMouseDown(event, planet) {
      if (planet.placed) return;
      this.draggingPlanet = planet;
      this.dragging = true;
      this.dragStartOffset = {
        x: event.offsetX,
        y: event.offsetY,
      };
      this.dragPosition = {
        x: event.clientX - this.dragStartOffset.x,
        y: event.clientY - this.dragStartOffset.y,
      };
    },
    onTouchStart(event, planet) {
      if (planet.placed) return;
      event.preventDefault();
      const touch = event.touches[0];
      const boundingRect = event.target.getBoundingClientRect();
      this.draggingPlanet = planet;
      this.dragging = true;
      this.dragStartOffset = {
        x: touch.clientX - boundingRect.left,
        y: touch.clientY - boundingRect.top,
      };
      this.dragPosition = {
        x: touch.clientX - this.dragStartOffset.x,
        y: touch.clientY - this.dragStartOffset.y,
      };
    },
    onMouseMove(event) {
      if (!this.dragging) return;
      this.dragPosition = {
        x: event.clientX - this.dragStartOffset.x,
        y: event.clientY - this.dragStartOffset.y,
      };
    },
    onTouchMove(event) {
      if (!this.dragging) return;
      event.preventDefault();
      const touch = event.touches[0];
      this.dragPosition = {
        x: touch.clientX - this.dragStartOffset.x,
        y: touch.clientY - this.dragStartOffset.y,
      };
    },
    onMouseUp(event) {
      if (!this.dragging) return;
      this.handleDrop(event.clientX, event.clientY);
      this.dragging = false;
      this.draggingPlanet = null;
    },
    onTouchEnd(event) {
      if (!this.dragging) return;
      event.preventDefault();
      const touch = event.changedTouches[0];
      this.handleDrop(touch.clientX, touch.clientY);
      this.dragging = false;
      this.draggingPlanet = null;
    },
    handleDrop(clientX, clientY) {
      // Find the drop zone under the position
      const dropZones = document
        .elementsFromPoint(clientX, clientY)
        .filter((el) => el.classList.contains("planet-dropzone"));
      if (dropZones.length > 0 && this.draggingPlanet) {
        const dropZone = dropZones[0];
        const dropPlanetName = dropZone.dataset.planetName;
        const dropPlanet = this.currentPlanets.find(
          (planet) => planet.name === dropPlanetName
        );
        this.attemptPlacement(this.draggingPlanet, dropPlanet);
      } else {
        this.message = "❌ Try Again!";
        this.playSound(
          "https://www.soundjay.com/button/sounds/beep-10.mp3"
        );
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    attemptPlacement(draggedPlanet, dropPlanet) {
      if (draggedPlanet.name === dropPlanet.name && !dropPlanet.placed) {
        dropPlanet.placed = true;
        draggedPlanet.placed = true;
        this.message = "✅ Correct!";
        this.playSound(
          "https://www.soundjay.com/buttons/sounds/button-16.mp3"
        );
        this.speakOutLoud(draggedPlanet.name);
        // Check if the current group is complete
        if (this.currentPlanets.every((planet) => planet.placed)) {
          if (this.currentGroupIndex < this.planetGroups.length - 1) {
            // Move to the next group after a short delay
            setTimeout(() => {
              this.currentGroupIndex++;
              this.setupGame();
            }, 1000);
          } else {
            // All groups completed
            this.isGameComplete = true;
            setTimeout(() => {
              this.message = "";
              this.playSound(
                "https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3"
              );
              this.speakOutLoud("Great Job!");
            }, 2000);
            setTimeout(() => {
              this.$router.push("/planets");
            }, 4000);
          }
        } else {
          setTimeout(() => {
            this.message = "";
          }, 2000);
        }
      } else {
        this.message = "❌ Try Again!";
        this.playSound(
          "https://www.soundjay.com/button/sounds/beep-10.mp3"
        );
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    getPlanetStyle(planet) {
      if (planet.placed) {
        return { opacity: 0.5 };
      } else if (this.draggingPlanet === planet && this.dragging) {
        return {
          visibility: "hidden",
        };
      } else {
        return null;
      }
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.addEventListener("error", function (e) {
        console.error("Error playing sound:", e);
      });
      audio.play().catch(function (error) {
        console.error("Error playing sound:", error);
      });
    }
  },
};
</script>

<style scoped>
.planets-drag-drop-game {
  text-align: center;
  margin: 20px;
}

.game-area {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

/* Left Side: Draggable Planets */
.draggable-planets {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30%;
}

.planet-draggable {
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 15px;
  padding: 10px;
  margin: 10px;
  width: 100px;
  cursor: grab;
  transition: transform 0.3s;
  user-select: none;
}

.planet-draggable:active {
  cursor: grabbing;
}

.planet-draggable img {
  width: 50px;
  height: 50px;
}

.planet-draggable span {
  display: block;
  margin-top: 5px;
  font-size: 1rem;
}

/* Right Side: Drop Zones */
.drop-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30%;
}

.planet-dropzone {
  width: 100px;
  height: 100px;
  margin: 10px;
}

.planet-placeholder {
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border: 2px dashed #2196f3;
}

.placed-planet {
  width: 100%;
  height: 100%;
}

/* Overall Styling */
.game-area {
  background-image: url("https://static.vecteezy.com/system/resources/thumbnails/044/229/340/small_2x/starry-night-sky-space-seamless-pattern-galaxy-shiny-stars-background-fabric-seamless-print-or-textile-background-wrapping-paper-space-pattern-or-wallpaper-with-comet-glowing-constellations-vector.jpg");
  background-size: cover;
  background-position: center;
  padding: 20px;
  border-radius: 20px;
}

.message {
  font-size: 2rem;
  font-weight: bold;
  margin-top: 20px;
}

/* Optional styles for the dragging clone element */
.dragging-clone {
  pointer-events: none;
}

/* Ensure two columns layout on mobile */
@media (max-width: 768px) {
  .game-area {
    flex-direction: row;
  }
  .draggable-planets,
  .drop-area {
    width: 50%;
  }
  .planet-draggable {
    width: 80px;
    margin: 5px;
  }
  .planet-draggable img {
    width: 40px;
    height: 40px;
  }
  .planet-draggable span {
    font-size: 0.9rem;
  }
  .planet-dropzone {
    width: 80px;
    height: 80px;
    margin: 5px;
  }
  .planet-placeholder,
  .placed-planet {
    width: 100%;
    height: 100%;
  }
}
</style>